import React, { useEffect } from "react";
import { Input, Select, Form, DatePicker, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  createAssetThunk,
  updateAssetThunk,
} from "../../../../Store/Slices/Finance/fixedAssets/fixedAssets.thunk";
import { fetchCategory } from "../../../../Store/Slices/Finance/Category/financeCategory.Thunk";


const FixedAssetsForm = ({ onClose, onSuccess, editData }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation("prtFinance");
  const categories = useSelector((state) => state.admin.financialCategory.categories);
  const { loading } = useSelector((store) => store.admin.fixedAssets);
  const isView = editData?.mode === "view";
  const isEdit = editData && !isView;

  // Watch assetValueType to auto-lock ratePerYear for Fixed
  const assetValueType = Form.useWatch("assetValueType", form);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        ...editData,
        purchaseDate: editData.purchaseDate ? dayjs(editData.purchaseDate) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ assetValueType: "Fixed", ratePerYear: 0 });
    }
  }, [editData, onClose]);

  // Auto-set ratePerYear to 0 when Fixed is selected
  useEffect(() => {
    if (assetValueType === "Fixed") {
      form.setFieldValue("ratePerYear", 0);
    }
  }, [assetValueType]);
  useEffect(() => {
    dispatch(fetchCategory({ categoryType: "asset", search: "", page: 1, limit: 10000 }));
  }, [])

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      purchaseDate: values.purchaseDate?.toISOString(),
      ratePerYear: values.assetValueType === "Fixed" ? 0 : (values.ratePerYear || 0),
    };

    if (isEdit) {
      await dispatch(updateAssetThunk({ id: editData._id, data: payload })).then((res) => {
        if (res?.payload?.success) { onSuccess?.(); onClose(); }
      });
    } else {
      await dispatch(createAssetThunk(payload)).then((res) => {
        if (res?.payload?.success) { onSuccess?.(); onClose(); }
      });
    }
  };

  // Computed preview of total cost (qty × unit price)
  const quantity = Form.useWatch("quantity", form);
  const unitPrice = Form.useWatch("unitPrice", form);
  const totalCost = (quantity || 0) * (unitPrice || 0);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className="space-y-1"
    >
      {/* Asset Name + Asset Code */}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          name="assetName"
          label={t("Asset Name")}
          rules={[{ required: true, message: t("Please enter asset name") }]}
        >
          <Input placeholder={t("e.g. Dell Laptop")} disabled={isView} />
        </Form.Item>

        <Form.Item name="assetCode" label={t("Asset Code")}>
          <Input placeholder={t("e.g. ASSET-001")} disabled={isView} />
        </Form.Item>
      </div>

      {/* Category + Asset Value Type */}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          name="category"
          label={t("Category")}
          rules={[{ required: true, message: t("Please select category") }]}
        >
          <Select
            disabled={isView}
            placeholder={t("Select category")}
          >
            {categories.map((cat) => (
              <Select.Option key={cat._id} value={cat._id}>
                {cat.categoryName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="assetValueType" label={t("Value Type")} initialValue="Fixed">
          <Select disabled={isView} placeholder={t("Select type")}>
            <Select.Option value="Fixed">{t("Fixed")}</Select.Option>
            <Select.Option value="Appreciating">{t("Appreciating")}</Select.Option>
            <Select.Option value="Depreciating">{t("Depreciating")}</Select.Option>
          </Select>
        </Form.Item>
      </div>

      {/* Quantity + Unit */}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          name="quantity"
          label={t("Quantity")}
          rules={[{ required: true, message: t("Please enter quantity") }]}
        >
          <InputNumber
            className="w-full"
            min={0}
            placeholder={t("e.g. 10")}
            disabled={isView}
          />
        </Form.Item>

        <Form.Item
          name="unit"
          label={t("Unit")}
          rules={[{ required: true, message: t("Please enter unit") }]}
        >
          <Input placeholder={t("e.g. Piece, Acre, Sq ft")} disabled={isView} />
        </Form.Item>
      </div>

      {/* Unit Price + Rate Per Year */}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          name="unitPrice"
          label={t("Unit Price")}
          rules={[{ required: true, message: t("Please enter unit price") }]}
        >
          <InputNumber
            className="w-full"
            min={0}
            placeholder={t("e.g. 250")}
            disabled={isView}
          />
        </Form.Item>

        <Form.Item
          name="ratePerYear"
          label={t("Rate (%) per Year")}
          tooltip={t("0 for Fixed assets. Appreciation or depreciation rate applied annually.")}
        >
          <InputNumber
            className="w-full"
            min={0}
            max={100}
            placeholder="0"
            disabled={isView || assetValueType === "Fixed"}
          />
        </Form.Item>
      </div>

      {/* Total Cost (read-only computed preview) */}
      {!isView && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 mb-1 flex justify-between items-center">
          <span className="text-sm text-gray-500">{t("Total Purchase Cost")}</span>
          <span className="font-bold text-purple-700">
            {totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      )}

      {/* Purchase Date */}
      <Form.Item
        name="purchaseDate"
        label={t("Purchase Date")}
        rules={[{ required: true, message: t("Select purchase date") }]}
      >
        <DatePicker className="w-full" disabled={isView} />
      </Form.Item>

      {/* Description */}
      <Form.Item name="description" label={t("Description")}>
        <Input.TextArea
          rows={3}
          placeholder={t("Optional notes about this asset")}
          disabled={isView}
        />
      </Form.Item>

      {/* SUBMIT */}
      {!isView && (
        <button
          type="submit"
          disabled={loading}
          className="btn-gradient mt-4 text-white w-full py-2  font-semibold hover:opacity-90 transition disabled:opacity-60"
        >
          {isEdit
            ? loading ? t("Updating...") : t("Update Asset")
            : loading ? t("Adding...") : t("Add Asset")}
        </button>
      )}
    </Form>
  );
};

export default FixedAssetsForm;
